/* Hallmark · component: Combobox · genre: modern-minimal · theme: maeiz-tokens */
import * as Popover from "@radix-ui/react-popover";
import React, { useMemo, useState } from "react";
import { clsx } from "clsx";
import { Check, ChevronsUpDown, Search } from "lucide-react";
import { Button } from "../Button";
import { overlayCollision, overlayContentMotionClass, thinScrollbarClass } from "../../styles/overlay";

export interface ComboboxOption {
    value: string;
    label: string;
    disabled?: boolean;
}

export interface ComboboxProps {
    options: ComboboxOption[];
    value?: string;
    onChange?: (value: string) => void;
    placeholder?: string;
    searchPlaceholder?: string;
    emptyMessage?: string;
    disabled?: boolean;
    className?: string;
    id?: string;
}

/** Searchable single-select (also exported as SearchableSelect). */
export const Combobox: React.FC<ComboboxProps> = ({
    options,
    value,
    onChange,
    placeholder = "Select…",
    searchPlaceholder = "Search…",
    emptyMessage = "No results",
    disabled,
    className,
    id,
}) => {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");

    const selected = options.find((o) => o.value === value);
    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return options;
        return options.filter((o) => o.label.toLowerCase().includes(q) || o.value.toLowerCase().includes(q));
    }, [options, query]);

    return (
        <Popover.Root
            open={open}
            onOpenChange={(next) => {
                setOpen(next);
                if (!next) setQuery("");
            }}
        >
            <Popover.Trigger asChild disabled={disabled}>
                <Button
                    id={id}
                    type="button"
                    variant="outline"
                    disabled={disabled}
                    aria-expanded={open}
                    className={clsx(
                        "w-full justify-between font-normal",
                        !selected && "text-neutral-secondary",
                        className,
                    )}
                    icon={ChevronsUpDown}
                    iconPosition="end"
                >
                    <span className="truncate text-start">{selected?.label ?? placeholder}</span>
                </Button>
            </Popover.Trigger>
            <Popover.Portal>
                <Popover.Content
                    align="start"
                    sideOffset={6}
                    avoidCollisions={overlayCollision.avoidCollisions}
                    collisionPadding={overlayCollision.collisionPadding}
                    className={clsx(
                        "z-[80] w-[var(--radix-popover-trigger-width)] min-w-[220px] overflow-hidden rounded-xl border border-neutral-border bg-white shadow-mz-lg outline-none dark:border-white/12 dark:bg-neutral-navy",
                        overlayContentMotionClass,
                    )}
                    onOpenAutoFocus={(e) => {
                        e.preventDefault();
                        const el = (e.currentTarget as HTMLElement).querySelector<HTMLInputElement>("input");
                        el?.focus();
                    }}
                >
                    <div className="flex items-center gap-2 border-b border-neutral-border px-3 py-2.5 dark:border-white/10">
                        <Search size={16} className="text-primary dark:text-gold-light" aria-hidden />
                        <input
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder={searchPlaceholder}
                            className="w-full bg-transparent text-sm text-neutral-ink outline-none placeholder:text-neutral-secondary dark:text-white"
                        />
                    </div>
                    <ul className={clsx("max-h-56 p-1", thinScrollbarClass)} role="listbox">
                        {filtered.length === 0 ? (
                            <li className="px-2.5 py-3 text-sm text-neutral-secondary">{emptyMessage}</li>
                        ) : (
                            filtered.map((opt) => (
                                <li key={opt.value}>
                                    <button
                                        type="button"
                                        role="option"
                                        aria-selected={opt.value === value}
                                        disabled={opt.disabled}
                                        className={clsx(
                                            "flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-start text-sm",
                                            "hover:bg-neutral-light disabled:opacity-40 dark:hover:bg-white/10",
                                            opt.value === value &&
                                                "bg-primary/10 font-medium text-primary dark:bg-gold/15 dark:text-gold-light",
                                        )}
                                        onClick={() => {
                                            onChange?.(opt.value);
                                            setOpen(false);
                                        }}
                                    >
                                        <Check
                                            size={14}
                                            className={clsx(
                                                "shrink-0 text-primary dark:text-gold-light",
                                                opt.value === value ? "opacity-100" : "opacity-0",
                                            )}
                                            aria-hidden
                                        />
                                        {opt.label}
                                    </button>
                                </li>
                            ))
                        )}
                    </ul>
                </Popover.Content>
            </Popover.Portal>
        </Popover.Root>
    );
};

export const SearchableSelect = Combobox;
